import React from 'react';
import { KpiTile } from '~analytics-components/kpi-tile';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import { formatPercentValue } from './split-form.helpers';
import type { KpiFormProps, KpiSplitSide, KpiTrend } from '../types';
import style from './split-form.module.scss';

/** The tile owns the tween for a raw number, but a percent side hands it a
 * pre-formatted string, so the 0..100 value is tweened here instead. */
const PercentSide: React.FC<{
    side: { label: string; value: number; trend?: KpiTrend };
}> = ({ side }) => {
    const tweenedValue = useTweenedValue(side.value);

    return (
        <KpiTile
            kpi={{
                title: side.label,
                value: formatPercentValue(tweenedValue),
                trend: side.trend,
            }}
        />
    );
};

/**
 * Split form: two labelled sub-values side by side (e.g. "RPP" vs "Full app"),
 * each a boxed `KpiTile` carrying its own value + trend
 * footnote below. Percent sides are pre-formatted to a string; count and currency
 * sides pass the raw number so `KpiTile` owns their formatting (same as number-form).
 */
const Side: React.FC<{ side: KpiSplitSide }> = ({ side }) => {
    // A cohort we cannot source yet (e.g. the RPP side until a breakdown is
    // emitted): show a placeholder in place of a value, with no trend.
    if ('unavailable' in side) {
        return <KpiTile kpi={{ title: side.label, value: 'N/A' }} />;
    }
    return side.unit === 'percent' ? (
        <PercentSide side={side} />
    ) : (
        <KpiTile
            kpi={{
                title: side.label,
                value: side.value,
                isCurrency: side.isCurrency,
                trend: side.trend,
            }}
        />
    );
};

export const SplitForm: React.FC<KpiFormProps> = ({ data }) => {
    if (data.form !== 'split') return null;
    return (
        <div className={style.split_form}>
            <div className={style.split_sides}>
                <Side side={data.left} />
                <Side side={data.right} />
            </div>
        </div>
    );
};
